import { useState } from "react";
import { Link } from "react-router";
import { Button, DataTable, type ColumnDef } from "@diametral/design-system/react";

import { useCancelRequest, useRequests } from "../api/requests";
import type { MembershipRequest } from "../api/types";
import { RequestStatusTag } from "../components/Badges";
import { ButtonLink } from "../components/ButtonLink";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Page } from "../components/Page";
import { PlainText } from "../components/PlainText";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../components/States";
import { RequirePerson } from "../layout/Guards";
import { formatDate, formatDateTime } from "../lib/format";
import { t, useTranslation } from "../lib/i18n";
import { REQUEST_KIND } from "../lib/labels";
import { notifySuccess } from "../lib/toast";

function decisionCell(request: MembershipRequest) {
  if (request.status === "pending") return <span className="ada-faint">{t("Awaiting the DAR")}</span>;
  return (
    <div className="ada-stack ada-stack--tight">
      {request.decision_message ? <PlainText text={request.decision_message} /> : null}
      <span className="ada-faint">
        {request.decided_at ? formatDateTime(request.decided_at) : ""}
        {request.decided_by ? ` · ${request.decided_by}` : ""}
      </span>
      {request.effective_date ? <span>{t("Effective date: {{date}}", { date: formatDate(request.effective_date) })}</span> : null}
    </div>
  );
}

export function MyRequestsPage() {
  const { t } = useTranslation();
  return (
    <Page
      kicker={t("My space")}
      title={t("My requests")}
      description={t("Your requests to join or leave a project, and the DAR's answer.")}
    >
      <RequirePerson>
        <MyRequests />
      </RequirePerson>
    </Page>
  );
}

function MyRequests() {
  const { t } = useTranslation();
  const requests = useRequests({ mine: true });
  const cancel = useCancelRequest();
  const [cancelling, setCancelling] = useState<MembershipRequest | null>(null);

  const columns: ColumnDef<MembershipRequest, unknown>[] = [
    {
      accessorKey: "created_at",
      header: t("Date"),
      cell: ({ row }) => <span className="ada-nowrap">{formatDateTime(row.original.created_at)}</span>,
    },
    {
      accessorKey: "kind",
      header: t("Request"),
      cell: ({ row }) => REQUEST_KIND[row.original.kind],
    },
    {
      accessorKey: "project_title",
      header: t("Project"),
      cell: ({ row }) => (
        <div className="ada-stack ada-stack--tight">
          <Link className="ada-link" to={`/projects/${row.original.project}`}>
            {row.original.project_title}
          </Link>
          <span className="ada-faint">{row.original.stream ? t("Stream “{{stream}}”", { stream: row.original.stream }) : t("Whole project")}</span>
        </div>
      ),
    },
    {
      id: "message",
      header: t("My message"),
      enableSorting: false,
      cell: ({ row }) => <PlainText text={row.original.message} />,
    },
    {
      accessorKey: "status",
      header: t("Status"),
      cell: ({ row }) => <RequestStatusTag status={row.original.status} />,
    },
    {
      id: "decision",
      header: t("DAR's answer"),
      enableSorting: false,
      cell: ({ row }) => decisionCell(row.original),
    },
    {
      id: "actions",
      header: () => <span className="sr-only">{t("Actions")}</span>,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.status === "pending" ? (
          <Button size="sm" variant="outline" onClick={() => setCancelling(row.original)}>
            {t("Cancel")}
            <span className="sr-only"> {t("the request on {{project}}", { project: row.original.project_title })}</span>
          </Button>
        ) : null,
    },
  ];

  if (requests.isPending) return <LoadingBlock lines={5} label={t("Loading your requests…")} />;
  if (requests.isError) return <ErrorBlock error={requests.error} onRetry={() => void requests.refetch()} />;

  return (
    <>
      {requests.data.length ? (
        <div className="ada-table-wrap">
          <DataTable columns={columns} data={requests.data} rowKey={(r) => String(r.id)} />
        </div>
      ) : (
        <EmptyBlock
          icon="list"
          title={t("No requests")}
          description={t("To join a project, open its page and click “Ask to join”.")}
        >
          <ButtonLink variant="primary" to="/projects">
            {t("See projects")}
          </ButtonLink>
        </EmptyBlock>
      )}
      <ConfirmDialog
        open={cancelling !== null}
        onOpenChange={(open) => (open ? undefined : setCancelling(null))}
        title={t("Cancel this request?")}
        description={
          cancelling
            ? t("Your request to {{action}} {{project}} will be withdrawn.", {
                action: REQUEST_KIND[cancelling.kind].toLowerCase(),
                project: cancelling.project_title,
              })
            : ""
        }
        confirmLabel={t("Cancel the request")}
        cancelLabel={t("Keep the request")}
        danger
        pending={cancel.isPending}
        onConfirm={() => {
          if (!cancelling) return;
          cancel.mutate(cancelling.id, {
            onSuccess: () => {
              notifySuccess(t("Request cancelled"));
              setCancelling(null);
            },
          });
        }}
      />
    </>
  );
}
